/**
 * DeleteActionCard - Content for CRM object deletion actions.
 *
 * Renders the rich ContentComponent (via ActionEntityPreview) inside an
 * ObjectCard with destructive variant — same pattern as every other v2
 * action card. The display name opens the CRM slideout when the entity
 * exists in CRM. An explicit warning sits below the entity preview.
 *
 * Shell is provided by the parent. Read-only.
 */

import { AlertCircle } from '@untitledui/icons';

import { isObjectDeletedPayload } from '../../lib/proposals/core/types';
import type { ActionRendererProps } from '../../lib/proposals/registry/types';
import { ActionEntityPreview } from './ActionEntityPreview';
import { extractProperties } from './CreateActionCard.helpers';
import { ActionCardContent, ActionError, getEntityTypeLabel } from './shared';

export function DeleteActionCard({
  action,
  mode,
  enrichedData,
  orgId,
}: ActionRendererProps) {
  if (!isObjectDeletedPayload(action.payload)) {
    return (
      <ActionError message='Invalid object deletion payload' mode={mode} />
    );
  }

  const { entity_type_name, entity_id } = action.payload;
  const entityType = enrichedData?.entity_type_name || entity_type_name;
  // Fallback label uses only the entity type — never a raw/truncated
  // entity id.
  const label = enrichedData?.object_label || getEntityTypeLabel(entityType);

  const crmEntityId = entity_id;

  // Map entity type → name property so the content component can render the
  // entity's label even when the entity isn't in the CRM store.
  const ENTITY_NAME_FIELDS: Record<string, string> = {
    ahoy_contact: 'ahoy_first_name',
    ahoy_company: 'ahoy_company_name',
    ahoy_deal: 'ahoy_deal_name',
    ahoy_risk: 'ahoy_title',
  };
  const nameField = ENTITY_NAME_FIELDS[entityType];
  // Use enrichedData.current_values (backend snapshot) as fallback props so
  // deleted entities that are no longer in CRM render all available fields,
  // not just the name. current_values comes through as `{ key: { value: ... } }`
  // wrappers, which ActionEntityPreview expects in flat form — run it through
  // extractProperties to unwrap before merging.
  //
  // `label` is the backend-composed display name (e.g. "Jane Doe") and we
  // only use it as a fallback for the name field when current_values
  // doesn't supply the real per-field value. Overwriting an existing
  // `ahoy_first_name = "Jane"` with the full label would cause the
  // contact card to render `"Jane Doe Doe"` (first_name + last_name).
  const extractedCurrentValues = extractProperties(
    enrichedData?.current_values ?? {}
  );
  const fallbackFlatProps = !crmEntityId
    ? {
        ...(nameField && extractedCurrentValues[nameField] === undefined
          ? { [nameField]: label }
          : {}),
        ...extractedCurrentValues,
      }
    : undefined;

  return (
    <ActionCardContent>
      {/* Entity being deleted — destructive styling reinforces the warning.
          ActionEntityPreview applies the bordered destructive card itself
          for registered types, and EntityCard's own variant styling covers
          the unregistered fallback, so no outer ObjectCard wrapper is
          needed here (wrapping would double-nest the fallback). */}
      <ActionEntityPreview
        entityId={crmEntityId}
        entityType={entityType}
        label={label}
        enrichedData={enrichedData}
        orgId={orgId}
        fallbackFlatProps={fallbackFlatProps}
        variant='destructive'
      />

      {/* Permanent deletion warning */}
      <div className='flex items-start gap-2 text-xs text-error-700'>
        <AlertCircle className='w-4 h-4 shrink-0 mt-0.5' aria-hidden='true' />
        <span>
          This {getEntityTypeLabel(entityType).toLowerCase()} will be
          permanently deleted. This action cannot be undone.
        </span>
      </div>
    </ActionCardContent>
  );
}
